/* eslint-disable @typescript-eslint/no-magic-numbers */

import { KeyboardEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import '@/styles/scrollbar.css';

import dropDownIcon from '@/Assets/Images/drop-down-icon.svg?url';
import { CategoryName } from '@/pages/TaskPlayer/types';
import { cn } from '@/utils';

interface Option {
  name: CategoryName;
  value: string;
}

function CategoryDropdown({
  onCategorySelect,
  selectedCategory,
}: {
  onCategorySelect: (category: string) => void;
  selectedCategory: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('adminLogin');
  const options = useMemo<Option[]>(
    () => [
      { name: CategoryName.OopsWeCannotLocateTheModule, value: t('cannotLocateModule') },
      { name: CategoryName.InvalidSDCard, value: t('invalidSdCard') },
      { name: CategoryName.SubjectThumbnailNotShowing, value: t('thumbnailNotShowing') },
      { name: CategoryName.CGSlateIsNotResponding, value: t('cgSlateNotResponding') },
      { name: CategoryName.CGSlateAppIsNotShowing, value: t('cgSlateAppNotShowing') },
      { name: CategoryName.LicenseRequired, value: t('licenseRequired') },
      { name: CategoryName.InvalidAdminCode, value: t('invalidAdminCode') },
      { name: CategoryName.SendReportsFailed, value: t('sendReportsFailed') },
      { name: CategoryName.VideoGetsPaused, value: t('videoGetsPaused') },
      { name: CategoryName.UnableToConnectInternet, value: t('unableToConnectInternet') },
      { name: CategoryName.UnableToCreateAccount, value: t('unableToCreateAccount') },
      { name: CategoryName.ChapterIsLocked, value: t('chapterIsLocked') },
      { name: CategoryName.IncorrectAnswer, value: t('notShowingRightAnswer') },
      { name: CategoryName.AllOptionsIncorrect, value: t('allOptionsIncorrect') },
      { name: CategoryName.VideoGlitches, value: t('videoHaveGlitches') },
      { name: CategoryName.PretestNotShowing, value: t('pretestNotShowing') },
      { name: CategoryName.ForgotAdminPin, value: t('forgotAdminPin') },
      { name: CategoryName.ForgotStudentPin, value: t('forgotStudentPin') },
      { name: CategoryName.UnableToSyncDevice, value: t('unableToSyncDevice') },
      { name: CategoryName.UnableToOpenChapter, value: t('unableToOpenChapter') },
      { name: CategoryName.UnableToOpenVideo, value: t('unableToOpenVideo') },
      { name: CategoryName.GradeNotUpgraded, value: t('gradeNotUpgrade') },
      { name: CategoryName.LearningDataLoss, value: t('learningDataLoss') },
      { name: CategoryName.Other, value: t('other') },
    ],
    [t],
  );

  const onChooseCategory = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const onHandleOptionSelect = useCallback(
    (option: CategoryName, event: React.MouseEvent | React.KeyboardEvent) => {
      event.stopPropagation();
      setIsOpen(false);
      onCategorySelect(option);
    },
    [onCategorySelect],
  );

  const onCategoryKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        onChooseCategory();
      }
    },
    [onChooseCategory],
  );
  const onOptionKeyDown = useCallback(
    (option: CategoryName) => (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        onHandleOptionSelect(option, event);
      }
    },
    [onHandleOptionSelect],
  );

  const chosenCategory = useMemo(() => {
    return selectedCategory
      ? options.find((opt) => opt.name === (selectedCategory as CategoryName))?.value
      : t('selectIssue');
  }, [selectedCategory, options, t]);

  const getCategoryOptions = () => {
    return (
      <div className="absolute z-modal flex max-h-[267px] w-full flex-col rounded-2xl border-2 border-neutral-300 bg-white px-4 shadow-lg">
        <div className="overflow-y-scroll scroll-smooth scrollbar">
          <style>
            {`
              .scrollbar::-webkit-scrollbar-thumb {
                background: #999999;
                border-radius: 9999px;
              }
              .scrollbar::-webkit-scrollbar-thumb:hover {
                background: #999999;
              }
              .scrollbar::-webkit-scrollbar-track {
                background: #DADADA;
                border-radius: 999px;
              }
              .scrollbar::-webkit-scrollbar-track:hover {
                background: #DADADA;
              }
            `}
          </style>
          {options.map((option, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index}>
              <div
                tabIndex={0}
                role="button"
                className="mr-4 cursor-pointer rounded-xl p-4 hover:bg-gray-100"
                onClick={(event) => onHandleOptionSelect(option.name, event)}
                onKeyDown={(event) => onOptionKeyDown(option.name)(event)}
              >
                {option.value}
              </div>
              {index < options.length - 1 && <hr className="mr-6 border-t border-neutral-300" />}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="mt-4 w-full">
      <div
        role="button"
        tabIndex={0}
        className={cn(
          'relative w-full cursor-pointer resize-none rounded-2xl border-2 border-neutral-grey-3 p-3 text-lg font-light',
          selectedCategory ? 'text-black' : 'text-neutral-grey-3',
        )}
        onClick={onChooseCategory}
        onKeyDown={onCategoryKeyDown}
      >
        <img
          className={cn(
            'absolute right-4 top-1/2 -translate-y-1/2 transform transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
          src={dropDownIcon}
          alt="drop-down"
        />
        {chosenCategory}
      </div>
      <div className="relative mt-2 w-full">{isOpen && getCategoryOptions()}</div>
    </div>
  );
}

export default CategoryDropdown;
